import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { PreviewButton } from "@/components/PreviewButton";
import { LineItemsEditor, computeLineTotal } from "@/components/LineItemsEditor";
import { Plus, CheckCircle, Upload, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { DiscussButton } from "@/components/DiscussButton";

const EMPTY = { budget_request_id: "", quotation_id: "", supplier_id: "", supplier_name: "", client_id: "", client_name: "", delivery_address: "", delivery_date: "", items: [], vat_status: "vatable", vat_amount: 0, payment_terms: "", notes: "", attachments: [] };

const VAT_STATUS_OPTIONS = [
  { value: "vatable", label: "Vatable (12%)" },
  { value: "zero_rated", label: "Zero-Rated (0%)" },
  { value: "exempt", label: "VAT-Exempt" },
];

function authedFileHref(url) {
  if (!url) return "#";
  if (url.startsWith("/api/files/")) {
    const t = localStorage.getItem("access_token") || "";
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${encodeURIComponent(t)}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  // Auto-compute Input VAT at 12% of the line-item subtotal when VAT Status
  // is Vatable. Manual override supported (any hand-typed value flips the
  // mode off; a "Reset to 12%" link restores auto).
  const [vatAutoMode, setVatAutoMode] = useState(true);
  useEffect(() => {
    if (!vatAutoMode || form.vat_status !== "vatable") return;
    const subtotal = computeLineTotal(form.items);
    const auto = Math.round(subtotal * 0.12 * 100) / 100;
    setForm((prev) => (Number(prev.vat_amount) === auto ? prev : { ...prev, vat_amount: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items, form.vat_status, vatAutoMode]);

  async function load() {
    const [p, q, s, c] = await Promise.all([
      api.get("/purchase-orders"),
      api.get("/quotations"),
      api.get("/suppliers"),
      api.get("/clients"),
    ]);
    setPos(p.data); setQuotations(q.data); setSuppliers(s.data); setClients(c.data);
  }

  useEffect(() => { load(); }, []);

  const canManage = can(user, ["procurement"]);
  const approvedQuotations = quotations.filter((q) => q.status === "completed" || q.is_selected);

  function openCreate() {
    setForm(EMPTY); setVatAutoMode(true); setOpen(true);
  }

  function loadFromQuotation(qid) {
    const q = quotations.find((x) => x.id === qid);
    if (!q) return;
    setForm({
      ...form,
      quotation_id: qid,
      budget_request_id: q.budget_request_id || "",
      supplier_id: q.supplier_id || "",
      supplier_name: q.supplier_name,
      client_id: q.client_id || form.client_id,
      client_name: q.client_name || form.client_name,
      items: q.items || [],
    });
  }

  async function submit(e) {
    e.preventDefault();
    const subtotal = computeLineTotal(form.items);
    const vat = form.vat_status === "vatable" ? (Number(form.vat_amount) || 0) : 0;
    const payload = { ...form, vat_amount: vat, total_amount: subtotal };
    try {
      await api.post("/purchase-orders", payload);
      toast.success("Purchase order issued");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  }

  async function markComplete(pid) {
    try {
      await api.post(`/purchase-orders/${pid}/status`, { status: "completed" });
      toast.success("PO marked completed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function uploadFiles(files) {
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE}/uploads/purchase_orders`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("upload failed");
        const j = await res.json();
        uploaded.push(j);
      }
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 02</div>
          <h1 className="section-title text-3xl sm:text-4xl">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">Auto-generated PO numbers, linked to budgets & quotations.</p>
        </div>
        {canManage && <Button onClick={openCreate} data-testid="new-po-btn"><Plus size={16} className="mr-2" /> Issue PO</Button>}
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1200px]">
          <TableHeader>
            <TableRow>
              <TableHead>PO Number</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Delivery Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Issued By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pos.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No purchase orders yet.</TableCell></TableRow>
            ) : pos.map((p) => (
              <TableRow key={p.id} data-testid={`po-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.po_number}</TableCell>
                <TableCell className="font-medium">{p.supplier_name}</TableCell>
                <TableCell className="text-sm">{p.delivery_date || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">₱{(p.total_amount || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.issued_by_name}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {canManage && p.status === "in_progress" && (
                      <Button size="sm" variant="outline" onClick={() => markComplete(p.id)} className="border-border" data-testid={`complete-po-${p.id}`}>
                        <CheckCircle size={14} className="mr-1" /> Mark complete
                      </Button>
                    )}
                    <PreviewButton endpointPath={`/exports/purchase-order/${p.id}`} baseName={`po_${p.po_number}`} title={`Purchase Order — ${p.po_number}`} testId={`preview-po-${p.id}`} />
                    <ExportMenu endpointPath={`/exports/purchase-order/${p.id}`} baseName={`po_${p.po_number}`} testId={`export-po-${p.id}`} />
                    <DiscussButton recordType="purchase_order" recordId={p.id} label={p.po_number} testId={`discuss-po-${p.id}`} />
                    {(user?.role === "admin" || p.issued_by === user?.id) && !p.deleted_at && (
                      <ArchiveButton endpoint={`/purchase-orders/${p.id}`} label={p.po_number} onArchived={load} testId={`archive-po-${p.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">Issue Purchase Order</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">From Quotation (optional)</Label>
                <Select value={form.quotation_id} onValueChange={loadFromQuotation}>
                  <SelectTrigger data-testid="po-quotation-select"><SelectValue placeholder="Pre-fill from approved quote" /></SelectTrigger>
                  <SelectContent>
                    {approvedQuotations.map((q) => <SelectItem key={q.id} value={q.id}>{q.quotation_no} — {q.supplier_name} — ₱{(q.total_amount || 0).toLocaleString()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Supplier</Label>
                <Select value={form.supplier_id} onValueChange={(v) => { const sp = suppliers.find((s) => s.id === v); setForm({ ...form, supplier_id: v, supplier_name: sp?.name || form.supplier_name }); }}>
                  <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Or type supplier name" required className="border-border" data-testid="po-supplier-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Delivery Address</Label>
                <Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="border-border" data-testid="po-address-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Delivery Date</Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="border-border" data-testid="po-date-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Linked Client (optional)</Label>
              <Select value={form.client_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_id: v, client_name: cl?.name || "" }); }}>
                <SelectTrigger data-testid="po-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <LineItemsEditor items={form.items} onChange={(it) => setForm({ ...form, items: it })} />
            {/* VAT block — mirrors Receivables auto-12% pattern. Applies to */}
            {/* Vatable POs only; Zero-Rated / Exempt lock the input to 0. */}
            <div className="p-3 border border-border rounded bg-muted/30 grid grid-cols-2 gap-3" data-testid="po-vat-row">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">VAT Status</Label>
                <Select
                  value={form.vat_status}
                  onValueChange={(v) => {
                    if (v === "vatable") setVatAutoMode(true);
                    setForm({ ...form, vat_status: v, vat_amount: v === "vatable" ? form.vat_amount : 0 });
                  }}
                >
                  <SelectTrigger data-testid="po-vat-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">VAT (₱)</Label>
                  {form.vat_status === "vatable" && (
                    vatAutoMode ? (
                      <span className="text-[9px] uppercase tracking-widest text-emerald-700" data-testid="po-vat-auto-badge">Auto · 12%</span>
                    ) : (
                      <button type="button" className="text-[9px] uppercase tracking-widest text-muted-foreground underline decoration-dotted hover:text-foreground" onClick={() => setVatAutoMode(true)} data-testid="po-vat-reset-auto-btn">Reset to 12%</button>
                    )
                  )}
                </div>
                <Input
                  type="number" min="0" step="0.01"
                  disabled={form.vat_status !== "vatable"}
                  value={form.vat_status === "vatable" ? form.vat_amount : 0}
                  onChange={(e) => { setVatAutoMode(false); setForm({ ...form, vat_amount: e.target.value }); }}
                  placeholder="0.00"
                  className="border-border tabular-nums font-heading disabled:opacity-60"
                  data-testid="po-vat-input"
                />
              </div>
              <div className="col-span-2 flex items-end justify-between border-t border-border pt-3">
                <div className="text-xs text-muted-foreground">Subtotal · <span className="tabular-nums text-foreground" data-testid="po-subtotal-display">₱{computeLineTotal(form.items).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Gross Total</div>
                  <div className="tabular-nums font-heading font-black text-lg" data-testid="po-grand-total-display">
                    ₱{(computeLineTotal(form.items) + (form.vat_status === "vatable" ? Number(form.vat_amount) || 0 : 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payment Terms</Label>
              <Textarea rows={2} value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="border-border" data-testid="po-terms-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attachments</Label>
              <label className="inline-flex items-center gap-2 text-xs border border-dashed border-border rounded px-3 py-2 hover:bg-muted cursor-pointer" data-testid="po-upload-label">
                <Upload size={14} />
                {uploading ? "Uploading…" : "Attach files (PDF, image, etc.)"}
                <input type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(Array.from(e.target.files))} data-testid="po-upload-input" />
              </label>
              {(form.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.attachments.map((a, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 text-xs border border-border rounded px-2 py-1 bg-muted/40" data-testid={`po-attachment-${i}`}>
                      <Paperclip size={12} />
                      <a href={authedFileHref(a.file_url)} target="_blank" rel="noreferrer" className="hover:underline">{a.file_name}</a>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-foreground">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="po-submit-btn">Issue PO</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
